<template>
  <layout>
    <page-header>
      <template slot="header">
        <h4 class="mr-4">
          <i class="icon-stack mr-2"></i>
          {{ $t('bookings.jobs_title') }}
        </h4>
      </template>
    </page-header>
    <div class="content">
      <admin-table v-if="currentDivision" ref="table" v-bind="tableProps">
        <template slot="route" slot-scope="{ item }">
          <div>
            <b-badge v-if="item.is_return" variant="info">
              <i class="icon-enter5"></i>
            </b-badge>
            <country-flag :country="item.route.origin.country" />
            <b>From:</b>
            {{ item.origin ? item.origin.name : item.route.origin.address }}
          </div>
          <div>
            <country-flag :country="item.route.destination.country" />
            <b>To:</b>
            {{
              item.destination
                ? item.destination.name
                : item.route.destination.address
            }}
          </div>
        </template>
        <template slot="company" slot-scope="{ item }">
          <router-link
            v-if="item.company"
            :to="{ name: 'companies.edit', params: { id: item.company.id } }"
          >
            {{ item.company.name }}
          </router-link>
        </template>
        <template slot="driver" slot-scope="{ item }">
          <router-link
            v-if="item.driver"
            :to="{ name: 'drivers.edit', params: { id: item.driver.id } }"
          >
            {{ item.driver.name }}
          </router-link>
        </template>
        <template slot="vehicle" slot-scope="{ item }">
          <img
            v-if="item.vehicle.image && item.vehicle.image.url"
            :src="item.vehicle.image.url"
            class="w-32 max-w-full mr-3"
          />
          <div class="text-xs">{{ item.vehicle.name }}</div>
        </template>
      </admin-table>
    </div>

    <ActionModal
      id="block-modal"
      :loading="modalBusy"
      :title="$t('bookings.block')"
      custom-reason
      @confirm="onConfirmBlock"
    />
    <CompanyPenaltyModal
      id="company-penalty-modal"
      :busy="modalBusy"
      :title="$t('bookings.company_penalty')"
      :currency="activeItem && activeItem.order.currency"
      @ok="onConfirmCompanyPenalty"
    />
  </layout>
</template>

<script>
import { AdminTable } from 'stylemix-vue-admin'
import moment from 'moment'
import { create } from 'vue-modal-dialogs'
import CountryFlag from 'vue-country-flag'

import ActionModal from './ActionModal.vue'
import Assigning from './Assigning'
import BookingsApi from './Api'
import BookingsIndexMixin from './BookingsIndexMixin'
import CompanyPenaltyModal from './CompanyPenaltyModal.vue'

const resourceBase = 'bookings'

export default {
  name: 'BookingsTasks',

  components: {
    ActionModal,
    AdminTable,
    CompanyPenaltyModal,
    CountryFlag,
  },

  mixins: [BookingsIndexMixin],

  data() {
    return {
      // Page header actions
      actions: [],
      modalBusy: false,
      activeItem: null,
      searchData: {
        query: null,
        where: {
          number: null,
          company_id: null,
          driver_id: null,
          vehicle_id: null,
          ordered_at: {
            gte: moment()
              .subtract(1, 'day')
              .toISOString(),
          },
        },
      },
    }
  },

  computed: {
    // Table configuration
    tableProps() {
      return {
        base: resourceBase,
        apiBuilder: () => {
          return new BookingsApi({
            context: 'list',
            division_id: this.currentDivision.id,
            include: 'vehicle,vehicle.media,company,driver,origin,destination',
            'where[status]': 'processing,complete',
          })
        },
        sortBy: 'ordered_at',
        sortDesc: false,
        perPage: 30,
        columns: [
          {
            key: 'number',
            label: this.$t('bookings.number'),
            sortable: true,
          },
          {
            key: 'ordered_at',
            label: this.$t('bookings.pickup_date'),
            sortable: true,
            component: 'value-date',
            config: {
              stacked: true,
              zone: true,
              zoneAttribute: 'timezone',
            },
            tdClass: (value, key, item) => {
              return this.orderDateVariant(item)
            },
          },
          {
            key: 'route',
            label: this.$t('bookings.route'),
            tdClass: 'text-sm',
          },
          {
            key: 'company',
            label: this.$t('bookings.company'),
          },
          {
            key: 'driver',
            label: this.$t('bookings.driver'),
          },
          {
            key: 'vehicle',
            label: this.$t('bookings.vehicle_class'),
          },
        ],

        searchFields: this.searchFields,
        searchData: this.searchData,

        rowActions: item => {
          const actions = ['edit']
          // allow manually completing bookings in testing env
          if (
            this.$allows('administer') &&
            item.status === 'processing'
          ) {
            actions.push({
              label: 'Complete',
              variant: 'light',
              handler: () => {
                this.complete(item)
              },
            })
          }
          actions.push(...this.rowActions(item))

          return actions
        },

        bulkActions() {
          return []
        },
      }
    },

    searchFields() {
      return [
        {
          component: 'text-field',
          type: 'search',
          attribute: 'query',
          placeholder: this.$t('general.search'),
        },
        {
          component: 'text-field',
          attribute: 'where.number',
          placeholder: this.$t('bookings.number'),
        },
        {
          component: 'relation-field',
          attribute: 'where.company_id',
          placeholder: this.$t('bookings.company'),
          ajax: true,
          source: {
            url: 'companies',
            params: {
              division_id: this.currentDivision.id,
              context: 'options',
            },
          },
        },
        {
          component: 'relation-field',
          attribute: 'where.driver_id',
          placeholder: this.$t('bookings.driver'),
          ajax: true,
          source: {
            url: 'drivers',
            params: {
              division_id: this.currentDivision.id,
              context: 'options',
            },
          },
        },
        {
          component: 'relation-field',
          attribute: 'where.vehicle_id',
          placeholder: this.$t('bookings.vehicle_class'),
          ajax: true,
          preload: true,
          source: {
            url: 'vehicles',
            params: {
              division_id: this.currentDivision.id,
              context: 'options',
            },
          },
        },
        // {
        //   component: 'relation-field',
        //   attribute: 'where.owner_id',
        //   placeholder: this.$t('bookings.owner'),
        //   ajax: true,
        //   preload: true,
        //   source: {
        //     url: 'users',
        //     params: {
        //       division_id: this.currentDivision.id,
        //       context: 'options',
        //     },
        //   },
        // },
        {
          component: 'date-range-field',
          attribute: 'where.ordered_at',
          placeholder: this.$t('general.select_period'),
        },
      ].filter(field => {
        if (field.attribute !== 'where.company_id') {
          return true
        }

        return this.$allows('bookings.index')
      })
    },
  },

  watch: {
    currentDivision() {
      this.$refs.table && this.$refs.table.reset()
    },
  },

  created() {
    this.assignDialog = create(Assigning, 'booking')
  },

  methods: {
    async complete(booking) {
      if (!(await this.$alert.warning('Confirm completing')).value) {
        return
      }

      const promise = new BookingsApi().complete(booking.id).then(result => {
        this.$toast.success(result.message)
        this.$refs.table.refresh()
      })

      this.$uiBlocker(promise, this.$el)
    },
  },
}
</script>

<style scoped lang="scss">
.flag {
  transform: scale(0.35) !important;
  margin: -16px -16px -26px !important;
}
</style>
